import { useApolloClient, useSubscription } from "@apollo/client";
import { ConfigurationKey } from "@salon/types";
import { useMemo } from "react";
import { GET_SALON_CONFIGURATIONS } from "@/graphql/configuration";
import {
  ConfigurationSubscriptionFilters,
  SALON_CONFIGURATION_SUBSCRIPTION,
} from "@/graphql/configuration-subscription";
import { useAuthStore } from "@/lib/store/auth-enhanced";

interface UseConfigurationSubscriptionProps {
  salonId: string;
  keys?: ConfigurationKey[];
  categories?: string[];
  enabled?: boolean;
  onConfigurationChange?: (changedKeys: string[]) => void;
}

/**
 * Hook for subscribing to real-time configuration updates
 *
 * Simplified version that updates Apollo cache directly instead of Zustand store.
 * Apollo cache is the single source of truth for configuration data.
 *
 * Features:
 * - Automatically updates Apollo cache when changes occur
 * - Optional filtering by specific keys or categories
 * - Subscription lifecycle management
 * - Error handling and logging
 * - Optional callback for custom handling
 */
export function useConfigurationSubscription({
  salonId,
  keys,
  categories,
  enabled = true,
  onConfigurationChange,
}: UseConfigurationSubscriptionProps) {
  const client = useApolloClient();

  // Check if user is authenticated
  const { accessToken, user } = useAuthStore();
  const isAuthenticated = !!accessToken && !!user;

  // Memoize subscription variables
  const variables: { filters: ConfigurationSubscriptionFilters } = useMemo(
    () => ({
      filters: {
        salonId,
        ...(keys && keys.length > 0 && { keys }),
        ...(categories && categories.length > 0 && { categories }),
      },
    }),
    [salonId, keys, categories]
  );

  const { error, loading } = useSubscription(SALON_CONFIGURATION_SUBSCRIPTION, {
    variables,
    skip: !enabled || !salonId || !isAuthenticated,
    onData: ({ data: subscriptionData }) => {
      if (subscriptionData?.data?.salonConfigurationUpdated) {
        const update = subscriptionData.data.salonConfigurationUpdated;

        // Extract the category from the first changed key to update the right cache entry
        const category = update.changedKeys[0]?.split(".")[0];

        if (category) {
          // Update Apollo cache directly
          client.cache.updateQuery(
            {
              query: GET_SALON_CONFIGURATIONS,
              variables: { salonId, category },
            },
            (existingData) => {
              if (!existingData?.getSalonConfigurations) {
                // If no existing data, create new cache entry
                return {
                  getSalonConfigurations: {
                    __typename: "SalonConfigurationsResponse" as const,
                    configurations: update.configurations,
                  },
                };
              }

              // Merge updated configurations with existing ones
              const existingConfigs = existingData.getSalonConfigurations.configurations || [];
              const updatedConfigMap = new Map(
                update.configurations.map((config) => [config.key, config])
              );

              const mergedConfigs = existingConfigs.map((config) => {
                const updated = updatedConfigMap.get(config.key);
                if (updated) {
                  updatedConfigMap.delete(config.key);
                  return updated;
                }
                return config;
              });

              // Add any new configurations that weren't in the existing list
              updatedConfigMap.forEach((config) => {
                mergedConfigs.push(config);
              });

              return {
                getSalonConfigurations: {
                  ...existingData.getSalonConfigurations,
                  configurations: mergedConfigs,
                },
              };
            }
          );
        }

        // Call optional callback
        onConfigurationChange?.(update.changedKeys);
      }
    },
  });

  return {
    loading,
    error,
    connected: !loading && !error && isAuthenticated,
    isAuthenticationRequired: !isAuthenticated,
  };
}
